import styles from "./Order.module.scss";
import { Button, Checkbox, Form, Input, Row, Space } from "antd";
import { InputCard } from "../../inventory/component/InputCard";
import { translate } from "../../../core";
import TextArea from "antd/es/input/TextArea";
import { AddressType } from "../../contact/type/contact";
import { useContext, useState } from "react";
import { PageContextValue, QueryPageableRequest, QueryPageableResponse, } from "../../../core/type";
import { Order, OrderAddress, OrderEntry } from "../type";
import { OrderPageContext } from "./OrderPageContext";
import { gql, useLazyQuery } from "@apollo/client";
import { WeddingRegistry, WeddingRegistryFindRequest, } from "../../wedding-registry/type";
import { mapAddress } from "../model-mapper/create-address";
import { ZoomInOutlined } from "@ant-design/icons";
import { SpecialTextModal } from "../../../core/component/SpecialTextModal";

export const OrderFormAddresses = ({
  onSameAddressChange,
  shipping,
}: {
  onSameAddressChange: (checked: boolean) => void;
  shipping: OrderAddress | undefined;
}) => {
  const { form } =
    useContext<PageContextValue<Order, OrderEntry>>(OrderPageContext);
  const getTitle = (index: number): string => {
    const type = getType(index);
    return {
      [AddressType.Billing]: translate("address"),
      [AddressType.Shipping]: translate("ship", "address"),
    }[type];
  };

  const getType = (index: number) => {
    return form?.getFieldValue(["addresses", index, "type"]) as AddressType;
  };

  const [openSpecialTextModal, setOpenSpecialTextModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const [executeWeddingRegistryAddressQuery] = useLazyQuery<
    QueryPageableResponse<WeddingRegistry, "weddingRegistryFind">,
    QueryPageableRequest<WeddingRegistryFindRequest>
  >(gql`
    query WeddingRegistryAddressQuery($request: WeddingRegistryFindRequest!) {
      weddingRegistryFind(request: $request) {
        data {
          id
          customer {
            firstName
            lastName
          }
          addresses {
            type
            street
            city
            country
            state
            zipCode
            address1
            address2
          }
        }
      }
    }
  `);
  const handleCoupleAddressEnabledChange = async (enabled: boolean) => {
    const entry = form?.getFieldsValue() ?? ({} as OrderEntry);
    const { weddingRegistry, addresses } = entry;
    if (!weddingRegistry?.id) return;

    const newShippingAddress = {} as OrderAddress;

    if (!enabled) {
      const shippingIndex = (addresses ?? []).findIndex(
        (ad) => ad.type === AddressType.Shipping
      );
      if (shippingIndex !== -1 && shipping) {
        addresses[shippingIndex] = {
          ...shipping,
          coupleAddressEnabled: false,
        };
      }
      form?.setFieldsValue(entry);
      return;
    } else {
      const { data } = await executeWeddingRegistryAddressQuery({
        variables: {
          request: {
            take: 1,
            skip: 0,
            id: weddingRegistry.id,
          },
        },
      });

      const [entity] = data?.weddingRegistryFind.data ?? [];
      const shippingAddress = (entity?.addresses ?? []).find(
        (ad) => ad.type === AddressType.Shipping
      );

      mapAddress(
        {
          type: AddressType.Shipping,
          firstName: entity.customer.firstName,
          lastName: entity.customer.lastName,
          middleName: "",
          address1: shippingAddress?.address1 ?? "",
          address2: shippingAddress?.address2 ?? "",
          street: shippingAddress?.street ?? "",
          city: shippingAddress?.city ?? "",
          state: shippingAddress?.state ?? "",
          zipCode: shippingAddress?.zipCode ?? "",
          country: shippingAddress?.country ?? "",
          coupleAddressEnabled: true,
        },
        newShippingAddress
      );
    }

    entry.addresses = [
      addresses.find((ad) => ad.type === AddressType.Billing) ??
        ({ type: AddressType.Billing } as OrderAddress),
      newShippingAddress,
    ];

    form?.setFieldsValue(entry);
  };

  return (
    <>
      <InputCard
        contained
        className={styles["customerAddressWrapper"]}
        title={translate("address")}
        expandable={true}
        onExpand={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded && (
          <div className={styles.addresses}>
            <Form.List name="addresses">
              {(fields) => (
                <Row className="mt-3">
                  {[...fields]
                    .sort(
                      (a, b) =>
                        (getType(a.name) === AddressType.Billing ? 0 : 1) -
                        (getType(b.name) === AddressType.Billing ? 0 : 1)
                    )
                    .map((field) => (
                    <InputCard
                      key={field.key}
                      title={getTitle(field.name)}
                      className={styles.billingCard + " mb-3"}
                    >
                      <Space
                        direction="horizontal"
                        size="middle"
                        align="center"
                        className="justify-content-end"
                        style={{ height: "2rem" }}
                      >
                        {getType(field.name) === AddressType.Shipping && (
                          <>
                            <Form.Item
                              name={[
                                field.name,
                                "theShipmentAddressIsTheSameAsBilling",
                              ]}
                              valuePropName="checked"
                            >
                              <Checkbox onChange={(value) => {
                                console.log(value.target.checked);
                                onSameAddressChange(value.target.checked);
                              }}>
                                {translate("ship_to_same")}
                              </Checkbox>
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "coupleAddressEnabled"]}
                              valuePropName="checked"
                            >
                              <Checkbox
                                disabled={
                                  !form?.getFieldValue([
                                    "weddingRegistry",
                                    "id",
                                  ])
                                }
                                onChange={(e) =>
                                  handleCoupleAddressEnabledChange(
                                    e.target.checked
                                  )
                                }
                              >
                                {translate("bride_groom")}
                              </Checkbox>
                            </Form.Item>
                          </>
                        )}
                      </Space>
                      <div className="d-flex gap-2">
                        <Form.Item
                          label={translate("first_name")}
                          name={[field.name, "firstName"]}
                          className="w-50"
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          label={translate("last_name")}
                          name={[field.name, "lastName"]}
                          className="w-50"
                        >
                          <Input />
                        </Form.Item>
                      </div>

                      <Form.Item
                        label={translate("street")}
                        name={[field.name, "street"]}
                      >
                        <Input />
                      </Form.Item>
                      <div className="d-flex gap-2">
                        <Form.Item
                          label={translate("address", "1")}
                          name={[field.name, "address1"]}
                          className="w-50"
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          label={translate("address", "2")}
                          name={[field.name, "address2"]}
                          className="w-50"
                        >
                          <Input />
                        </Form.Item>
                      </div>

                      <div className="d-flex gap-2">
                        <Form.Item
                          label={translate("city")}
                          name={[field.name, "city"]}
                          className={styles.city}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          label={translate("state")}
                          name={[field.name, "state"]}
                          className={styles.state}
                        >
                          <Input style={{width: '7rem'}}/>
                        </Form.Item>
                        <Form.Item
                          label={translate("zip_code")}
                          name={[field.name, "zipCode"]}
                          className={styles.zipCode}
                        >
                          <Input style={{width: '7rem'}}/>
                        </Form.Item>
                        <Form.Item
                          label={translate("country")}
                          name={[field.name, "country"]}
                          className={styles.country}
                        >
                          <Input />
                        </Form.Item>
                      </div>
                    </InputCard>
                  ))}
                </Row>
              )}
            </Form.List>
            <InputCard
              title={translate("special_instruction")}
              className="mb-3 mt-4"
            >
              <Space>
                <Form.Item
                  name="print"
                  valuePropName="checked"
                  className="mt-1 mb-2"
                >
                  <Checkbox>{translate("show")}</Checkbox>
                </Form.Item>
                <Button
                  type="text"
                  shape="circle"
                  icon={<ZoomInOutlined />}
                  onClick={() => setOpenSpecialTextModal(true)}
                />
              </Space>

              <Form.Item name="specialInstruction">
                <TextArea size="large" rows={4} />
              </Form.Item>
            </InputCard>
          </div>
        )}
      </InputCard>
      <SpecialTextModal
        text={form?.getFieldValue(["specialInstruction"])}
        open={openSpecialTextModal}
        onCancel={() => setOpenSpecialTextModal(false)}
        onSend={(data) => {
          form?.setFieldsValue({
            specialInstruction: data.text,
          });
          setOpenSpecialTextModal(false);
        }}
      />
    </>
  );
};
